import React, { useState, useEffect } from 'react';
import { Checkbox, Input, Space, Typography, Divider, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FeatureService } from '../../services/api/featureService';
import { Feature } from '../../types/subscription';

const { Text } = Typography;
const { Search } = Input;

interface FeatureChecklistProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}

export const FeatureChecklist: React.FC<FeatureChecklistProps> = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    setLoading(true);
    try {
      const featureList = await FeatureService.getAll();
      setFeatures(featureList);
    } catch (error) {
      console.error('Error loading features:', error);
      message.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (featureId: string, checked: boolean) => {
    const newValue = checked
      ? [...value, featureId]
      : value.filter(id => id !== featureId);
    onChange?.(newValue);
  };

  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    const category = feature.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(feature);
    return acc;
  }, {} as Record<string, Feature[]>);

  // Filter features based on search
  const filteredGroupedFeatures = Object.entries(groupedFeatures).reduce(
    (acc, [category, categoryFeatures]) => {
      const filtered = categoryFeatures.filter(feature =>
        !searchText ||
        feature.code.toLowerCase().includes(searchText.toLowerCase()) ||
        feature.name.toLowerCase().includes(searchText.toLowerCase())
      );
      if (filtered.length > 0) {
        acc[category] = filtered;
      }
      return acc;
    },
    {} as Record<string, Feature[]>
  );

  const categories = Object.keys(filteredGroupedFeatures).sort();

  // Select/deselect all in a category
  const handleCategoryToggle = (category: string, select: boolean) => {
    const categoryFeatures = filteredGroupedFeatures[category] || [];
    const categoryIds = categoryFeatures.map(f => f.id);
    
    if (select) {
      // Add all category features that aren't already selected
      const combined = [...value, ...categoryIds];
      const newValue = Array.from(new Set(combined));
      onChange?.(newValue);
    } else {
      // Remove all category features
      const newValue = value.filter(id => !categoryIds.includes(id));
      onChange?.(newValue);
    }
  };

  // Check if all features in a category are selected
  const isCategoryFullySelected = (category: string): boolean => {
    const categoryFeatures = filteredGroupedFeatures[category] || [];
    if (categoryFeatures.length === 0) return false;
    return categoryFeatures.every(f => value.includes(f.id));
  };

  // Check if some (but not all) features in a category are selected
  const isCategoryPartiallySelected = (category: string): boolean => {
    const categoryFeatures = filteredGroupedFeatures[category] || [];
    if (categoryFeatures.length === 0) return false;
    const selectedCount = categoryFeatures.filter(f => value.includes(f.id)).length;
    return selectedCount > 0 && selectedCount < categoryFeatures.length;
  };

  return (
    <Spin spinning={loading}>
      <div className="feature-checklist">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Search
            placeholder="Search features by code or name"
            allowClear
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 8 }}
            disabled={disabled}
          />

          {categories.length === 0 && !loading ? (
            <Text type="secondary">No features found</Text>
          ) : (
          categories.map((category, categoryIndex) => {
            const categoryFeatures = filteredGroupedFeatures[category] || [];
            const isFullySelected = isCategoryFullySelected(category);
            const isPartiallySelected = isCategoryPartiallySelected(category);

            return (
              <div key={category}>
                {categoryIndex > 0 && <Divider style={{ margin: '12px 0' }} />}
                <div style={{ marginBottom: 8 }}>
                  <Checkbox
                    indeterminate={isPartiallySelected}
                    checked={isFullySelected}
                    onChange={(e) => handleCategoryToggle(category, e.target.checked)}
                    disabled={disabled}
                    style={{ fontWeight: 'bold' }}
                  >
                    <Text strong>{category}</Text>
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                      ({categoryFeatures.filter(f => value.includes(f.id)).length} / {categoryFeatures.length} selected)
                    </Text>
                  </Checkbox>
                </div>
                <Space direction="vertical" size="small" style={{ width: '100%', marginLeft: 24 }}>
                  {categoryFeatures.map((feature) => (
                    <Checkbox
                      key={feature.id}
                      checked={value.includes(feature.id)}
                      onChange={(e) => handleFeatureChange(feature.id, e.target.checked)}
                      disabled={disabled}
                    >
                      <Space direction="vertical" size={0}>
                        <Text strong>{feature.code}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {feature.name}
                        </Text>
                      </Space>
                    </Checkbox>
                  ))}
                </Space>
              </div>
            );
          })
          )}
        </Space>
      </div>
    </Spin>
  );
};

